import { useCallback, useLayoutEffect, useState } from "react";

const INITIAL_STATE = {
  hasOverflow: false,
  canScrollBack: false,
  canScrollForward: false,
};

const useHorizontalOverflow = ({
  containerRef,
  dependency,
  enabled = true,
  scrollerRef,
}) => {
  const [overflowState, setOverflowState] = useState(INITIAL_STATE);

  const updateOverflow = useCallback(() => {
    if (!enabled) return;

    const container = containerRef.current;
    const scroller = scrollerRef.current;

    if (!container || !scroller) return;

    const hasOverflow = scroller.scrollWidth > container.clientWidth + 1;

    if (!hasOverflow && scroller.scrollLeft !== 0) {
      scroller.scrollLeft = 0;
    }

    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const nextState = {
      hasOverflow,
      canScrollBack: hasOverflow && scroller.scrollLeft > 1,
      canScrollForward:
        hasOverflow && scroller.scrollLeft < maxScrollLeft - 1,
    };

    setOverflowState((currentState) =>
      currentState.hasOverflow === nextState.hasOverflow &&
      currentState.canScrollBack === nextState.canScrollBack &&
      currentState.canScrollForward === nextState.canScrollForward
        ? currentState
        : nextState,
    );
  }, [containerRef, enabled, scrollerRef]);

  useLayoutEffect(() => {
    if (!enabled) {
      setOverflowState(INITIAL_STATE);
      return undefined;
    }

    const container = containerRef.current;
    const scroller = scrollerRef.current;

    if (!container || !scroller) return undefined;

    let animationFrame;
    let isActive = true;
    const scheduleUpdate = () => {
      if (!isActive) return;
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateOverflow);
    };

    updateOverflow();

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(container);
    resizeObserver.observe(scroller);
    Array.from(scroller.children).forEach((child) =>
      resizeObserver.observe(child),
    );

    window.addEventListener("resize", scheduleUpdate);
    document.fonts?.addEventListener?.("loadingdone", scheduleUpdate);
    document.fonts?.ready.then(scheduleUpdate);

    return () => {
      isActive = false;
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      document.fonts?.removeEventListener?.("loadingdone", scheduleUpdate);
    };
  }, [
    containerRef,
    dependency,
    enabled,
    scrollerRef,
    updateOverflow,
  ]);

  return {
    ...overflowState,
    updateOverflow,
  };
};

export default useHorizontalOverflow;
